import { useRef, useState } from 'react'
import { ColorResult, RGBColor, SketchPicker } from 'react-color'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { translations } from '../../../configs/translations'
import { Language } from '../../../context/LanguageContext'
import { ClientApi } from '../../../services/clientApi'
import { VehicleService } from '../../../services/VehicleService'
import { NuiCallbacks } from '../../../utils/ClientEvents'
import './PreviewMenu.css'

interface PreviewMenuProps {
	vehicleId: number
}

export const PreviewMenu = ({ vehicleId }: PreviewMenuProps) => {

	const [language, setLanguage] = useState<Language>('ru')
	const t = translations[language]

	const [primaryColor, setPrimaryColor] = useState<RGBColor>({ r: 0, g: 0, b: 0 })
	const [secondaryColor, setSecondaryColor] = useState<RGBColor>({ r: 0, g: 0, b: 0 })
	const [openPicker, setOpenPicker] = useState<'primary' | 'secondary' | null>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const primaryBlockRef = useRef<HTMLDivElement>(null)
	const secondaryBlockRef = useRef<HTMLDivElement>(null)

	const [markersVisible, setMarkersVisible] = useState<boolean>(true)
	const [actualView, setActualView] = useState<'interior' | 'exterior'>('exterior')
	const [doorsOpened, setDoorsOpened] = useState<boolean>(false)

	const COLOR_SEND_THROTTLE_MS = 100
	const lastColorSentAtRef = useRef(0)
	const pendingColorSendRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleToggleMarkers = () => {
		const markersState = !markersVisible
		ClientApi.send(NuiCallbacks.TOGGLE_PREVIEW_MARKERS, {
			state: markersState
		})
		setMarkersVisible(markersState)
	}

	const handleOpenAllDoors = () => {
		const doorsState = !doorsOpened
		ClientApi.send(NuiCallbacks.TOGGLE_PREVIEW_VEHICLE_DOORS, {
			state: doorsState
		})
		setDoorsOpened(doorsState)
	}

	const handleToggleView = () => {
		const viewState = actualView === 'exterior' ? 'interior' : 'exterior'
		ClientApi.send(NuiCallbacks.TOGGLE_PREVIEW_VEHICLE_CAMERA, {
			state: viewState
		})
		setActualView(viewState)
	}

	const togglePicker = (picker: 'primary' | 'secondary') => {
		setOpenPicker(prev => prev === picker ? null : picker)
	}

	const toRgbString = (color: RGBColor) => `rgb(${color.r}, ${color.g}, ${color.b})`

	const sendColorToClient = (picker: 'primary' | 'secondary', color: RGBColor) => {
		lastColorSentAtRef.current = Date.now()
		ClientApi.send(NuiCallbacks.SET_PREVIEW_VEHICLE_COLOR, {
			vehicleId,
			isColor1: picker === 'primary',
			r: color.r,
			g: color.g,
			b: color.b,
		})
	}

	const handleColorChange = (picker: 'primary' | 'secondary', setColor: (color: RGBColor) => void, color: RGBColor) => {
		setColor(color)

		const elapsed = Date.now() - lastColorSentAtRef.current
		if (elapsed >= COLOR_SEND_THROTTLE_MS) {
			if (pendingColorSendRef.current) {
				clearTimeout(pendingColorSendRef.current)
				pendingColorSendRef.current = null
			}
			sendColorToClient(picker, color)
			return
		}

		if (pendingColorSendRef.current) clearTimeout(pendingColorSendRef.current)
		pendingColorSendRef.current = setTimeout(() => {
			pendingColorSendRef.current = null
			sendColorToClient(picker, color)
		}, COLOR_SEND_THROTTLE_MS - elapsed)
	}

	const renderPalette = (
		picker: 'primary' | 'secondary',
		color: RGBColor,
		setColor: (color: RGBColor) => void,
		blockRef: React.RefObject<HTMLDivElement | null>
	) => {
		if (openPicker !== picker || !blockRef.current || !menuRef.current) return null
		const blockRect = blockRef.current.getBoundingClientRect()
		const menuRect = menuRef.current.getBoundingClientRect()
		return createPortal(
			<div className="preview-palette-container" style={{ top: blockRect.top - 10, left: menuRect.right }}>
				<SketchPicker
					disableAlpha={true}
					color={color}
					onChange={(result: ColorResult) => handleColorChange(picker, setColor, result.rgb)}
				/>
			</div>,
			document.body
		)
	}

	const handleTestDrive = () => {
		VehicleService.runTestDrive(
			vehicleId,
			{ r: primaryColor.r, g: primaryColor.g, b: primaryColor.b },
			{ r: secondaryColor.r, g: secondaryColor.g, b: secondaryColor.b }
		).catch((error) => {
			toast.error(
				error &&
					error.response &&
					error.response.data &&
					t[error.response.data as keyof typeof t]
					? t[error.response.data as keyof typeof t]
					: t.error_unknown,
				{ className: 'notification-error' }
			)
		})
	}

	return (<>
		<div className='preview-menu' ref={menuRef}>

			<div className='preview-menu-header'>
				<h1>{t.interaction}</h1>
			</div>

			<div className='preview-menu-content'>

				<div className='preview-menu-color-block-list'>
					<div className='preview-menu-color-block' ref={primaryBlockRef}>
						<p>{t.color_1}</p>
						<div
							className='preview-menu-color-icon'
							style={{ backgroundColor: toRgbString(primaryColor) }}
							onClick={() => togglePicker('primary')}
						/>
					</div>
					{renderPalette('primary', primaryColor, setPrimaryColor, primaryBlockRef)}

					<div className='preview-menu-color-block' ref={secondaryBlockRef}>
						<p>{t.color_2}</p>
						<div
							className='preview-menu-color-icon'
							style={{ backgroundColor: toRgbString(secondaryColor) }}
							onClick={() => togglePicker('secondary')}
						/>
					</div>
					{renderPalette('secondary', secondaryColor, setSecondaryColor, secondaryBlockRef)}
				</div>

				<div className='preview-menu-buttons-list'>

					<div className='preview-menu-button' onClick={handleToggleMarkers}>
						<p>{markersVisible ? t.turn_off_markers : t.turn_on_markers}</p>
					</div>


					<div className='preview-menu-button' onClick={handleToggleView}>
						<p>{actualView === 'exterior' ? t.look_at_interior : t.look_at_exterior}</p>
					</div>

					<div className='preview-menu-button' onClick={handleOpenAllDoors}>
						<p>{doorsOpened ? t.close_all_doors : t.open_all_doors}</p>
					</div>

					<div className='preview-menu-button' onClick={handleTestDrive}>
						<p>{t.test_drive}</p>
					</div>

				</div>

			</div>

		</div>

	</>)

}